/**
 * Wordloom rule constants: board geometry, the ORIGINAL premium-square layout,
 * the ORIGINAL tile distribution and letter values, and setting defaults.
 *
 * The layout and distribution are Wordloom's own designs (not copied from any
 * commercial game). Both are pinned by tests (counts + symmetry), so change
 * them deliberately.
 */
import type { GameSettings, PremiumKind } from './types';

export const BOARD_SIZE = 15;
/** Row/column index of the centre square (the start square). */
export const CENTER = 7;
export const RACK_SIZE = 7;
/** Bonus for playing all RACK_SIZE tiles in one move. */
export const BINGO_BONUS = 50;
/** An exchange is only allowed while at least this many tiles are in the bag. */
export const MIN_BAG_FOR_EXCHANGE = 7;
/** Longest accepted word (a full board row). */
export const MAX_WORD_LENGTH = BOARD_SIZE;

export const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

/**
 * Tile distribution: 100 tiles = 98 lettered + 2 blanks.
 * `value` is the face value in points. Blanks ('?') are worth 0.
 */
export const TILE_DISTRIBUTION: Readonly<Record<string, { count: number; value: number }>> = {
  A: { count: 9, value: 1 },
  B: { count: 2, value: 3 },
  C: { count: 2, value: 3 },
  D: { count: 4, value: 2 },
  E: { count: 12, value: 1 },
  F: { count: 2, value: 4 },
  G: { count: 3, value: 3 },
  H: { count: 3, value: 3 },
  I: { count: 8, value: 1 },
  J: { count: 1, value: 9 },
  K: { count: 1, value: 6 },
  L: { count: 4, value: 1 },
  M: { count: 2, value: 3 },
  N: { count: 6, value: 1 },
  O: { count: 8, value: 1 },
  P: { count: 2, value: 3 },
  Q: { count: 1, value: 10 },
  R: { count: 6, value: 1 },
  S: { count: 5, value: 1 },
  T: { count: 6, value: 1 },
  U: { count: 3, value: 2 },
  V: { count: 2, value: 5 },
  W: { count: 2, value: 4 },
  X: { count: 1, value: 8 },
  Y: { count: 2, value: 4 },
  Z: { count: 1, value: 10 },
};
export const BLANK_COUNT = 2;
export const TOTAL_TILES = 100;

/** Face value of a letter ('A'..'Z'); 0 for anything else (e.g. blanks). */
export const LETTER_VALUES: Readonly<Record<string, number>> = Object.fromEntries(
  Object.entries(TILE_DISTRIBUTION).map(([letter, { value }]) => [letter, value]),
);

/**
 * Premium squares, specified for ONE octant (0 <= row <= col <= CENTER) and
 * reflected across both diagonals and both centre lines to fill the board.
 *
 *   TW (0,3)                           ->  8 squares
 *   DW (1,6) (4,4) + centre (7,7)       -> 8 + 4 + 1 = 13 squares
 *   TL (0,0) (5,5) (3,7)                -> 4 + 4 + 4 = 12 squares
 *   DL (0,7) (1,2) (2,4) (6,6)          -> 4 + 8 + 8 + 4 = 24 squares
 *
 * The centre square is a DW and is also the mandatory start square (★).
 */
export const PREMIUM_OCTANT: ReadonlyArray<readonly [number, number, PremiumKind]> = [
  [0, 3, 'TW'],
  [1, 6, 'DW'],
  [4, 4, 'DW'],
  [7, 7, 'DW'],
  [0, 0, 'TL'],
  [5, 5, 'TL'],
  [3, 7, 'TL'],
  [0, 7, 'DL'],
  [1, 2, 'DL'],
  [2, 4, 'DL'],
  [6, 6, 'DL'],
];

function buildPremiumLayout(): (PremiumKind | null)[][] {
  const grid: (PremiumKind | null)[][] = Array.from({ length: BOARD_SIZE }, () =>
    Array<PremiumKind | null>(BOARD_SIZE).fill(null),
  );
  const last = BOARD_SIZE - 1;
  for (const [r, c, kind] of PREMIUM_OCTANT) {
    // All 8 symmetries of the square (duplicates collapse onto the same cell).
    const images: Array<[number, number]> = [
      [r, c], [c, r],
      [r, last - c], [c, last - r],
      [last - r, c], [last - c, r],
      [last - r, last - c], [last - c, last - r],
    ];
    for (const [ir, ic] of images) {
      const row = grid[ir]!;
      row[ic] = kind;
    }
  }
  return grid;
}

/** `PREMIUM_LAYOUT[row][col]` → premium kind or null. */
export const PREMIUM_LAYOUT: ReadonlyArray<ReadonlyArray<PremiumKind | null>> = buildPremiumLayout();

/** Multipliers per premium kind. */
export const PREMIUM_MULTIPLIER: Readonly<Record<PremiumKind, { letter: number; word: number }>> = {
  TW: { letter: 1, word: 3 },
  DW: { letter: 1, word: 2 },
  TL: { letter: 3, word: 1 },
  DL: { letter: 2, word: 1 },
};

/** Human labels for premium squares. */
export const PREMIUM_LABEL: Readonly<Record<PremiumKind, { short: string; long: string }>> = {
  TW: { short: '3W', long: 'Triple word' },
  DW: { short: '2W', long: 'Double word' },
  TL: { short: '3L', long: 'Triple letter' },
  DL: { short: '2L', long: 'Double letter' },
};

/** Turn timer choices offered in the lobby (seconds; null = untimed). */
export const TURN_TIME_OPTIONS: ReadonlyArray<number | null> = [null, 60, 120, 300];
/** "End after N passes each" choices offered in the lobby. */
export const PASS_ROUND_OPTIONS: ReadonlyArray<number> = [1, 2, 3];

export const DEFAULT_SETTINGS: Readonly<GameSettings> = {
  turnTimeLimitSec: null,
  passRoundsToEnd: 2,
};

/** Score thresholds for client celebrations. */
export const CELEBRATE_BIG_MOVE = 40;
export const CELEBRATE_GOOD_MOVE = 25;
